import { ModalState } from "../../../types/modals";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { api } from "../../api";
import { Image, Video } from "../../../types/admin";
import { changeCollectionModal } from "./collectionEditModal";

export type CollectionElementModalState = ModalState & {
    element_id: string | null;
    columnId: string;
    rowId: string;
    type: string;
    text: string;
    default_image: Image | null;
    language_image: Image | null;
    default_video: Video | null;
    language_video: Video | null;
    default_vimeo: string | null;
    language_vimeo: string | null;
    video_autoplay: boolean;
    video_loop: boolean;
    isSaving: boolean;
};

const initialState: CollectionElementModalState = {
    loading: true,
    isOpen: false,
    error: null,
    element_id: null,
    columnId: '',
    rowId: '',
    type: '',
    text: '',
    default_image: null,
    language_image: null,
    default_video: null,
    language_video: null,
    default_vimeo: null,
    language_vimeo: null,
    video_autoplay: false,
    video_loop: false,
    isSaving: false
};

export const saveElement = createAsyncThunk(
    'collectionElementModal/saveElement',
    async (payload: FormData, thunkAPI) => {
        const element_id = payload.get('element_id');
        const slide_id = payload.get('content_id');
        try {
            const dispatch = thunkAPI.dispatch;
            if (element_id) {
                const response = await api.patch(`/API/admin/collection-slides/${slide_id}/elements/${element_id}/`, payload, true);
                dispatch(changeCollectionModal({ slide_rows: response.slide_rows }));
                return response;
            } else {
                const response = await api.post(`/API/admin/collection-slides/${slide_id}/elements/`, payload, true);
                dispatch(changeCollectionModal({ slide_rows: response.slide_rows }));
                return response;
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type getElementPayload = {
    element_id: string;
    slide_id: string;
    language: string;
};

export const getElement = createAsyncThunk(
    'collectionElementModal/getElement',
    async (payload: getElementPayload, thunkAPI) => {
        const { element_id, slide_id } = payload;
        try {
            const response = api.get(`/API/admin/collection-slides/${slide_id}/elements/${element_id}/`, payload);
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type ModalPayload = {
    element_id: string | null;
    type: string;
    rowId: string;
    columnId: string;
};

const slice = createSlice({
    name: 'collectionElementModal',
    initialState,
    reducers: {
        closeModal: () => {
            return initialState;
        },
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            if (action.payload.element_id) {
                state.element_id = action.payload.element_id;
            } else {
                state.loading = false;
            }
            state.type = action.payload.type;
            state.rowId = action.payload.rowId;
            state.columnId = action.payload.columnId;
            state.isOpen = true;
        },
        changeModal(state, action) {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(saveElement.pending, (state) => {
            state.isSaving = true;
            state.error = null;
        });
        builder.addCase(saveElement.fulfilled, () => {
            return initialState;
        });
        builder.addCase(saveElement.rejected, (state, action) => {
            state.loading = false;
            state.isSaving = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(getElement.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getElement.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            Object.assign(state, action.payload);
        });
        builder.addCase(getElement.rejected, (state, action) => {
            state.loading = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
    }
});

export const { openModal, closeModal, changeModal } = slice.actions;
export default slice.reducer;
